//
// Copyright (c) 2011-2021 by Curalate, Inc.
//

import {
  ContentBlock,
  ContentState,
  EditorState,
  Modifier,
  SelectionState,
} from 'draft-js';

import { ISmartInputContext } from '@components/molecules/smart-input/SmartInputContext';
import { SmartInputTrackingId } from '@components/molecules/smart-input/smartInputTrackingIds';

export const getSmartInputTrackingId = (
  trackingPrefix: string,
  trackingId: SmartInputTrackingId,
) => {
  return `${trackingPrefix}${trackingId}`;
};

/**
 * Returns the ContentState and CollectionState for a given EditorState
 *
 * @param editorState The current EditorState
 */
export const getCurrentStateProperties = (editorState: EditorState) => {
  const currentContentState = editorState.getCurrentContent();
  const currentSelectionState = editorState.getSelection();

  return { currentContentState, currentSelectionState };
};

/**
 * Returns the content block for the given content and selection
 *
 * @param content The current ContentState
 * @param selection the current SelectionState
 */
export const getCurrentContentBlock = (
  content: ContentState,
  selection: SelectionState,
): ContentBlock => {
  const anchorKey = selection.getAnchorKey();
  const currentBlock = content.getBlockForKey(anchorKey);

  return currentBlock;
};

/**
 * Returns the character before the current selection
 *
 * @param content The current ContentState
 * @param selection the current SelectionState
 */
export const getCharBeforeSelection = (
  content: ContentState,
  selection: SelectionState,
) => {
  const currentBlock = getCurrentContentBlock(content, selection);
  const currentBlockText = currentBlock.getText();

  const startOffset = selection.getStartOffset();
  const str = currentBlockText.slice(0, startOffset);
  const charBefore = str[str.length - 1];

  return charBefore || '';
};

/**
 * Returns the character after the current selection
 *
 * @param content The current ContentState
 * @param selection the current SelectionState
 */
export const getCharAfterSelection = (
  content: ContentState,
  selection: SelectionState,
) => {
  const currentBlock = getCurrentContentBlock(content, selection);

  const endOffset = selection.getEndOffset();

  if (currentBlock.getLength() === endOffset) {
    return '';
  }

  const str = currentBlock.getText().slice(0, endOffset + 1);
  const charAfter = str[str.length - 1];

  return charAfter;
};

/**
 * Updates the current editor state with the text to be added
 *
 * @param text The text string to be added
 * @param smartInputContext
 */
export const insertText = (
  text: string,
  smartInputContext: ISmartInputContext,
) => {
  const editorState = smartInputContext.editor.getEditorState();

  const { currentContentState, currentSelectionState } =
    getCurrentStateProperties(editorState);

  /**
   * Replaces any currently selected text with the new text
   */
  const textContent = Modifier.replaceText(
    currentContentState,
    currentSelectionState,
    text,
  );

  const newEditorState = EditorState.push(
    editorState,
    textContent,
    'insert-fragment',
  );

  smartInputContext.editor.setEditorState(
    EditorState.forceSelection(newEditorState, textContent.getSelectionAfter()),
    true,
  );
};

/**
 * Replaces the entire content of the current editor with the specified text
 *
 * @param text The text string to replace the current editor content
 * @param smartInputContext The context of the SmartInput component containing the editor state
 */
export const replaceAllText = (
  text: string,
  smartInputContext: ISmartInputContext,
) => {
  const editorState = smartInputContext.editor.getEditorState();

  smartInputContext.editor.setIsProgrammaticChange(true);

  // Create a new content state with the entire text replaced
  const newContentState = ContentState.createFromText(text);

  // Create a new editor state with the content
  const newEditorState = EditorState.push(
    editorState,
    newContentState,
    'insert-fragment',
  );

  // Get the key of the last block
  const lastBlockKey = newContentState.getLastBlock().getKey();

  // Get the length of the new content's last block to set the cursor at the end
  const contentLength = newContentState.getLastBlock().getLength();

  // Create a new selection at the end of the last block
  const selection = SelectionState.createEmpty(lastBlockKey).merge({
    anchorOffset: contentLength,
    focusOffset: contentLength,
  });

  // Update the editor state with the new selection
  smartInputContext.editor.setEditorState(
    EditorState.forceSelection(newEditorState, selection),
  );
  smartInputContext.editor.setIsProgrammaticChange(false);
};
